import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  FolderKanban,
  Loader2,
  Paperclip,
  BookOpen,
  CheckSquare,
  UploadCloud,
  Trash2,
  Download,
  Plus,
  ChevronRight,
  ChevronDown,
  X,
  StickyNote,
} from "lucide-react";
import {
  useProject,
  useUploadFiles,
  useDeleteFile,
  downloadProjectFile,
  useCreateStory,
  useUpdateStory,
  useDeleteStory,
  useCriterio,
  type Prioridad,
  type StoryEstado,
  type UserStory,
} from "../api/projects";
import { TareasTab } from "../components/TareasTab";
import { PizarraTab } from "../components/PizarraTab";

type Tab = "resumen" | "archivos" | "stories" | "tareas" | "pizarra";

const fmtSize = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function ProyectoDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const projectId = Number(id);
  const { data: project, isLoading } = useProject(projectId);
  const [tab, setTab] = useState<Tab>("resumen");

  if (isLoading || !project) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Cargando proyecto…
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode; count?: number }[] = [
    { key: "resumen", label: "Resumen", icon: <FolderKanban size={15} /> },
    { key: "tareas", label: "Tareas", icon: <CheckSquare size={15} />, count: project.tareas_count },
    { key: "stories", label: "User Stories", icon: <BookOpen size={15} />, count: project.user_stories_count },
    { key: "pizarra", label: "Pizarra", icon: <StickyNote size={15} /> },
    { key: "archivos", label: "Archivos", icon: <Paperclip size={15} />, count: project.archivos_count },
  ];

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0 overflow-auto">
      <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-brand-700">
        <ArrowLeft size={15} /> Proyectos
      </Link>

      <header className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: project.color || "#0b4a2f" }}>
          <FolderKanban size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black text-slate-900">{project.nombre}</h1>
          {project.descripcion && <p className="text-slate-500 mt-1">{project.descripcion}</p>}
          <div className="mt-2 flex flex-wrap gap-3 text-xs font-semibold text-slate-500">
            <span className="rounded-full bg-slate-100 px-2.5 py-1">{project.estado}</span>
            {project.responsable && <span>Responsable: {project.responsable.nombre}</span>}
            {project.fecha_fin && <span>Entrega: {project.fecha_fin}</span>}
          </div>
        </div>
      </header>

      <nav className="flex gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${
              tab === t.key ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.icon} {t.label}
            {t.count != null && <span className="rounded-full bg-slate-100 px-1.5 text-xs">{t.count}</span>}
          </button>
        ))}
      </nav>

      <div className="min-h-0 flex-1">
        {tab === "resumen" && <ResumenTab project={project} />}
        {tab === "tareas" && <TareasTab projectId={projectId} stories={project.user_stories ?? []} />}
        {tab === "stories" && <StoriesTab projectId={projectId} />}
        {tab === "pizarra" && <PizarraTab projectId={projectId} />}
        {tab === "archivos" && <ArchivosTab projectId={projectId} />}
      </div>
    </div>
  );
}

function ResumenTab({ project }: { project: ReturnType<typeof useProject>["data"] & object }) {
  const stats = [
    { label: "Tareas", value: project.tareas_count ?? 0, icon: <CheckSquare size={18} /> },
    { label: "User Stories", value: project.user_stories_count ?? 0, icon: <BookOpen size={18} /> },
    { label: "Archivos", value: project.archivos_count ?? 0, icon: <Paperclip size={18} /> },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-3 max-w-2xl">
      {stats.map((s) => (
        <div key={s.label} className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-soft">
          <div className="text-brand-700">{s.icon}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">{s.value}</div>
          <div className="text-sm font-semibold text-slate-500">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Archivos ──────────────────────────────────────────── */

function ArchivosTab({ projectId }: { projectId: number }) {
  const { data: project } = useProject(projectId);
  const upload = useUploadFiles(projectId);
  const del = useDeleteFile(projectId);
  const archivos = project?.archivos ?? [];

  return (
    <div className="max-w-3xl flex flex-col gap-4">
      <label className="flex h-24 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/50 text-brand-700 font-bold hover:bg-brand-50">
        {upload.isPending ? <Loader2 className="animate-spin" size={18} /> : <UploadCloud size={18} />}
        Subir archivos
        <input
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) upload.mutate(files);
            e.target.value = "";
          }}
        />
      </label>

      {archivos.length === 0 ? (
        <p className="text-sm text-slate-400">Sin archivos todavía.</p>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 bg-white divide-y divide-slate-100">
          {archivos.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-4 py-3">
              <Paperclip size={16} className="text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{a.nombre_original}</div>
                <div className="text-xs text-slate-400">{fmtSize(a.tamano)}{a.autor ? ` · ${a.autor.nombre}` : ""}</div>
              </div>
              <button onClick={() => downloadProjectFile(a)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Descargar"><Download size={16} /></button>
              <button onClick={() => { if (confirm("¿Eliminar archivo?")) del.mutate(a.id); }} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50" title="Eliminar"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── User Stories ──────────────────────────────────────── */

const PRIO_BADGE: Record<Prioridad, string> = {
  alta: "bg-rose-50 text-rose-600",
  media: "bg-amber-50 text-amber-700",
  baja: "bg-slate-100 text-slate-500",
};

function StoriesTab({ projectId }: { projectId: number }) {
  const { data: project } = useProject(projectId);
  const update = useUpdateStory(projectId);
  const del = useDeleteStory(projectId);
  const [showCreate, setShowCreate] = useState(false);
  const stories = project?.user_stories ?? [];

  return (
    <div className="max-w-3xl flex flex-col gap-4">
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 text-sm font-bold">
          <Plus size={15} /> Nueva historia
        </button>
      </div>

      {showCreate && <CreateStoryModal projectId={projectId} onClose={() => setShowCreate(false)} />}

      {stories.length === 0 ? (
        <p className="text-sm text-slate-400">Sin historias de usuario.</p>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 bg-white divide-y divide-slate-100">
          {stories.map((s) => (
            <StoryRow key={s.id} projectId={projectId} story={s} onUpdate={update} onDelete={del} />
          ))}
        </div>
      )}
    </div>
  );
}

function CreateStoryModal({ projectId, onClose }: { projectId: number; onClose: () => void }) {
  const create = useCreateStory(projectId);
  const [form, setForm] = useState({ titulo: "", descripcion: "", prioridad: "media" as Prioridad });
  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.titulo.trim()) return;
    await create.mutateAsync({
      titulo: form.titulo.trim(),
      descripcion: form.descripcion || null,
      prioridad: form.prioridad,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-slate-900">Nueva historia de usuario</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Historia *</span>
          <input className={input} placeholder="Como [usuario] quiero… para…" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} autoFocus />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Descripción / contexto</span>
          <textarea className={`${input} min-h-[70px]`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Prioridad</span>
          <select className={input} value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value as Prioridad })}>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baja">Baja</option>
          </select>
        </label>
        <p className="text-xs text-slate-400">Tras crearla, despliega la historia para añadir sus criterios de aceptación.</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={create.isPending || !form.titulo.trim()} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
            {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear
          </button>
        </div>
      </form>
    </div>
  );
}

function StoryRow({
  projectId,
  story,
  onUpdate,
  onDelete,
}: {
  projectId: number;
  story: UserStory;
  onUpdate: ReturnType<typeof useUpdateStory>;
  onDelete: ReturnType<typeof useDeleteStory>;
}) {
  const criterio = useCriterio(projectId);
  const [open, setOpen] = useState(true);
  const [nuevo, setNuevo] = useState("");
  const criterios = story.criterios ?? [];
  const cumplidos = criterios.filter((c) => c.cumplido).length;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-700">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${PRIO_BADGE[story.prioridad]}`}>{story.prioridad}</span>
        <div className="min-w-0 flex-1 font-semibold text-slate-800 truncate">{story.titulo}</div>
        {criterios.length > 0 && (
          <span className="text-xs font-semibold text-slate-400">{cumplidos}/{criterios.length}</span>
        )}
        <select
          className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold"
          value={story.estado}
          onChange={(e) => onUpdate.mutate({ id: story.id, payload: { estado: e.target.value as StoryEstado } })}
        >
          <option value="pendiente">Pendiente</option>
          <option value="progreso">En progreso</option>
          <option value="hecha">Hecha</option>
        </select>
        <button onClick={() => { if (confirm("¿Eliminar historia?")) onDelete.mutate(story.id); }} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={15} /></button>
      </div>

      {open && (
        <div className="mt-2 ml-7 flex flex-col gap-1.5">
          <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">Criterios de aceptación</span>
          {criterios.map((c) => (
            <div key={c.id} className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={c.cumplido} onChange={() => criterio.toggle.mutate(c.id)} />
              <span className={c.cumplido ? "line-through text-slate-400" : "text-slate-700"}>{c.texto}</span>
              <button onClick={() => criterio.remove.mutate(c.id)} className="ml-auto text-rose-300 hover:text-rose-600"><X size={13} /></button>
            </div>
          ))}
          {criterios.length === 0 && <span className="text-xs text-slate-300">Sin criterios todavía.</span>}
          <form
            onSubmit={(e) => { e.preventDefault(); if (nuevo.trim()) { criterio.create.mutate({ storyId: story.id, texto: nuevo.trim() }); setNuevo(""); } }}
            className="flex gap-2 mt-1"
          >
            <input className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-sm" placeholder="Añadir criterio de aceptación…" value={nuevo} onChange={(e) => setNuevo(e.target.value)} />
            <button type="submit" className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-50"><Plus size={13} /></button>
          </form>
        </div>
      )}
    </div>
  );
}
